import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  BPEStore,
  Property,
  PropertyValue,
} from "../../BuildingProductEditor/ProductEditorStore";
import PropertyRowEditable from "./PropertyRowEditable";

import Alert from "components/Alert";
import { PropertyTypeChoices } from "api-client";
import { propertyManagerStore } from "features/PropertyManager/PropertyManagerStore";
import AddPropertyButton from "features/PropertyManager/components/AddPropertyButton";
import { COEStore } from "features/ComponentEditor/ComponentEditorStore";

const CustomProperties = (props: { isComponent?: boolean }) => {
  const { t } = useTranslation();
  const store = props.isComponent ? COEStore : BPEStore;
  const properties = [...store.data.properties];

  const sortableItems =
    properties?.map((row, index) => ({
      ...row,
      sortableId: `${row.slug}-${index}`,
    })) ?? [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sortableItems.findIndex(
        (item) => item.sortableId === active.id
      );
      const newIndex = sortableItems.findIndex(
        (item) => item.sortableId === over?.id
      );

      const reorderedItems = arrayMove(properties, oldIndex, newIndex);
      store.onChangeProperties(reorderedItems);
      await propertyManagerStore.updateOrdering(reorderedItems);
    }
  };

  const addProperty = (type: PropertyTypeChoices, value: PropertyValue) => {
    store.onChangeProperties([
      ...(properties ?? []),
      {
        id: String(properties?.length ?? 0),
        slug: "",
        property_type: type,
        name: "",
        value: value,
        options: [],
      },
    ]);
    propertyManagerStore.setLastIndexFocused(properties?.length);
  };

  const onChangeNameAndValue = (
    index: number,
    value: PropertyValue,
    key: keyof Property
  ) => {
    store.updatePropertyValues(index, key, value);
  };

  const handleRemoveProperty = async (id?: string, slug?: string) => {
    store.onChangeProperties(properties?.filter((item) => item.id !== id));
    if (slug) await propertyManagerStore.removeProperty(slug);
  };

  const handleChangeName = async (index: number) => {
    const current = properties[index];
    if (!current.name) return;
    const response = current.slug
      ? await propertyManagerStore.updateProperty(current)
      : await propertyManagerStore.createProperty(current);
    response && store.updatePropertyValues(index, "slug", response.slug);
  };

  return (
    <div className="py-6 border-b border-gray-200 xl:border-b-0">
      <div className="flex justify-between">
        <div className="text-base font-semibold mb-3">
          {t("productEditor.ownProperties")}
        </div>
        <AddPropertyButton addProperty={addProperty} />
      </div>
      {properties?.length ? (
        <div className="border border-gray-300 rounded-md text-sm font-medium">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableItems.map((item) => item.sortableId)}
            >
              {properties?.map((item, index) => (
                <PropertyRowEditable
                  key={index}
                  sortableId={
                    sortableItems.find((_, inx) => inx === index)?.sortableId
                  }
                  property={item}
                  index={index}
                  setTitle={(value) =>
                    onChangeNameAndValue(index, value, "name")
                  }
                  setValue={(value) =>
                    onChangeNameAndValue(index, value, "value")
                  }
                  onChangeName={() => handleChangeName(index)}
                  removeProperty={handleRemoveProperty}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <Alert
          type="info"
          description={t("productEditor.noPropertyAlert")}
          className="!bg-gray-50 border-none"
          iconClassName="flex items-center justify-center"
        />
      )}
    </div>
  );
};

export default observer(CustomProperties);
